import React, { createContext, useEffect, useMemo, useState } from "react";

//api
import { getAllUserGroups, getWorkspacesForUserGroup, removeUserGroupFromWorkspaceAccess, setOrganisationRoleForUserGroup } from  "@/api/orgSettings/userGroups";
import { getAllUsersAvailableToAdd, getAllUsersForGroup, addUserToOrganisationGroup, removeUserFromOrganisationGroup } from  "@/api/orgSettings/users";

//interfaces
import { NotificationStatus } from  "@/interfaces/languages";
import { PaginationSettings } from  "@/interfaces/lists";
import { AllUsersSortByType, IUserData, IUserGroupData, ResourcePermission } from  "@/interfaces/orgSettings";
import { SortBy } from  "@/interfaces/toggle";
import { IUserWorkspaceData, WorkspaceSortByType } from  "@/interfaces/workspaces";



export interface IOrgSettingsUserGroupsContext {
    userGroupId: string,
    userGroupName: string,
    setUserGroupId: (userId: string) => void,
    setUserGroupName: (userName: string) => void,
    getUserGroups: any;
    userGroupData: IUserGroupData[],
    workspaceData: IUserWorkspaceData[],
    getModalUsers: any;
    getUsers: any;
    userData: IUserData[],
    modalUserData: IUserData[],
    notificationDetails: {show: boolean, status: NotificationStatus, message: string},
    setNotificationDetails:any,
    paginationSettings: PaginationSettings,
    workspacePaginationSettings: PaginationSettings,
    sortToggle: [AllUsersSortByType, SortBy],
    setSortToggle: ([AllUsersSortByType, SortBy]) => void,
    usersSortToggle: [AllUsersSortByType, SortBy],
    setUsersSortToggle: ([AllUsersSortByType, SortBy]) => void,
    modalSortToggle: [AllUsersSortByType, SortBy],
    setModalSortToggle: ([AllUsersSortByType, SortBy]) => void,
    workspacesSortToggle: [WorkspaceSortByType, SortBy],
    setWorkspacesSortToggle: ([WorkspacesSortByType, SortBy]) => void,
    searchString: string,
    setSearchString: (search: string) => void,
    searchModalUsersString: string,
    setSearchModalUsersString: (search: string) => void,
    setUserGroupPermission:any,
    includeArchivedUserGroups: boolean,
    setIncludeArchivedUserGroups: (include: boolean) => void,
    setIsLoading: (loading: boolean) => void,
    isLoading: boolean,
    setIsModalLoading: (loading: boolean) => void,
    isModalLoading: boolean,
    setIsWorkspacesLoading: (loading: boolean) => void,
    isWorkspacesLoading: boolean,
    moreAvailable: boolean,
    removeUserFromGroup: any,
    addUserToGroup: any,
    groupCanBeModified: boolean,
    setGroupCanBeModified: any,
    getWorkspacesForGroup: any,
    removeGroupFromWorkspace: any
}

export const orgSettingsUserGroupsDefaults:IOrgSettingsUserGroupsContext = {
    userGroupId: undefined,
    userGroupName: undefined,
    setUserGroupId:() => {},
    setUserGroupName:() => {},
    getUserGroups: () => {},
    userGroupData: undefined,
    getUsers: () => {},
    getModalUsers: () => {},
    userData: undefined,
    workspaceData: undefined,
    modalUserData: undefined,
    notificationDetails: {show: false, status: null, message: ""},
    setNotificationDetails:() => {},
    paginationSettings: {hasPagination: false, currentPage: 1, totalPages:1, pageSize: 20, totalItems: 0},
    workspacePaginationSettings: {hasPagination: false, currentPage: 1, totalPages:1, pageSize: 20, totalItems: 0},
    sortToggle: [AllUsersSortByType.PERMISSIONS, SortBy.DESCENDING],
    setSortToggle: () => {},
    usersSortToggle: [AllUsersSortByType.USERNAME, SortBy.DESCENDING],
    setUsersSortToggle: () => {},
    modalSortToggle: [AllUsersSortByType.FIRST_NAME, SortBy.NONE],
    setModalSortToggle: () => {},
    workspacesSortToggle: [WorkspaceSortByType.WORKSPACE, SortBy.ASCENDING],
    setWorkspacesSortToggle: () => {},
    searchString: "",
    setSearchString: () => {},
    searchModalUsersString: "",
    setSearchModalUsersString: () => {},
    setUserGroupPermission: () => {},
    includeArchivedUserGroups: undefined,
    setIncludeArchivedUserGroups:() => {},
    isLoading: true,
    setIsLoading:() => {},
    isModalLoading: true,
    setIsWorkspacesLoading:() => {},
    isWorkspacesLoading: true,
    setIsModalLoading:() => {},
    moreAvailable: false,
    removeUserFromGroup: () => {},
    addUserToGroup: () => {},
    groupCanBeModified: true,
    setGroupCanBeModified: () => {},
    getWorkspacesForGroup: () => {},
    removeGroupFromWorkspace: () => {}
}

export const OrgSettingsUserGroupsContext = createContext(orgSettingsUserGroupsDefaults);

const OrgSettingsUserGroupsProvider = ({children}:{children:React.ReactNode}) => {
    const top : number = 20;  
    const [userGroupId, setUserGroupId] = useState<string>("");
    const [userGroupName, setUserGroupName] = useState<string>("");
    const [userGroupData, setUserGroupData] = useState<IUserGroupData[]>(undefined);
    const [userData, setUserData] = useState<IUserData[]>(undefined);
    const [modalUserData, setModalUserData] = useState<IUserData[]>(undefined);
    const [notificationDetails, setNotificationDetails] = useState<{show: boolean, status: NotificationStatus, message: string}>({show: false, status: null, message: ""});
    const [includeArchivedUserGroups, setIncludeArchivedUserGroups] = useState<boolean>(false);
    const [searchString, setSearchString] = useState<string>("");
    const [searchModalUsersString, setSearchModalUsersString] = useState<string>("");
    const [sortToggle, setSortToggle] = useState<[AllUsersSortByType, SortBy]>([AllUsersSortByType.PERMISSIONS, SortBy.DESCENDING]);
    const [usersSortToggle, setUsersSortToggle] = useState<[AllUsersSortByType, SortBy]>([AllUsersSortByType.USERNAME, SortBy.ASCENDING]);
    const [modalSortToggle, setModalSortToggle] = useState<[AllUsersSortByType, SortBy]>([AllUsersSortByType.USERNAME, SortBy.ASCENDING]);
    const [workspacesSortToggle, setWorkspacesSortToggle] = useState<[WorkspaceSortByType, SortBy]>([WorkspaceSortByType.WORKSPACE, SortBy.ASCENDING]);
    const [paginationSettings, setPaginationSettings] = useState<PaginationSettings>({hasPagination: false, currentPage: 1, totalPages:1, pageSize: 20, totalItems: 0});
    const [workspacePaginationSettings, setWorkspacePaginationSettings] = useState<PaginationSettings>({hasPagination: false, currentPage: 1, totalPages:1, pageSize: 20, totalItems: 0});
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isModalLoading, setIsModalLoading] = useState<boolean>(true);
    const [isWorkspacesLoading, setIsWorkspacesLoading] = useState<boolean>(true);
    const [abortController, setAbortController] = useState<AbortController>(null);
    const [moreAvailable, setMoreAvailable] = useState<boolean>(false);
    const [modalSkip, setModalSkip] = useState<number>(0);
    const [groupCanBeModified, setGroupCanBeModified] = useState<boolean>(true);
    const [workspaceData, setWorkspaceData] = useState<IUserWorkspaceData[]>(undefined);
    
    useEffect(() => {
        if (notificationDetails.show) {
            const timer = setTimeout(() => {
                setNotificationDetails({show: false, status:null, message: ""});
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [notificationDetails]);

    function setUserGroupPermission(groupId: number, selectedRole: ResourcePermission){
        const params = {
            permission: selectedRole === ResourcePermission.MANAGE ? 'manage' : 'read'
        }
        setOrganisationRoleForUserGroup(groupId, params)
        .then(() => {
            setNotificationDetails({ show: true, status: NotificationStatus.success, message: "Successfully updated Organisation Role for user group."});
        })
        .catch(() => {
            setNotificationDetails({ show: true, status: NotificationStatus.error, message: "Unable to update the Organisation Role for user group."});
        })
        .finally(() => {
            getUserGroups();
        })
    }

    function getUserGroups(page?: number) {
        if (abortController) { abortController.abort(); }
    
        const newAbortController = new AbortController();
        setAbortController(newAbortController);
        setIsLoading(true);

        let skip = 0;
        if (page) { skip = (page - 1) * top; }
    
        let params = {
            filters: includeArchivedUserGroups ? ['includeInactive'] : [],
            searchTerm: searchString,
            sortBy: sortToggle[0],
            sort: sortToggle[1],
            top: top,
            skip: skip,
        };
    
        getAllUserGroups(params, { signal: newAbortController.signal })
            .then((data) => {
                setUserGroupData(data.records);
                setPaginationSettings({
                    hasPagination: data.total > data.top,
                    currentPage: Math.ceil(page ?? 1),
                    totalPages: Math.ceil(data.total / data.top),
                    pageSize: data.top,
                    totalItems: data.total,
                });
            })
            .catch((error) => {
                if (error.name !== "AbortError" && error.code !== "ERR_CANCELED") {
                    setNotificationDetails({show: true, status: NotificationStatus.error, message: "Unable to get user group data, please refresh the page."});
                }
            });
    }

    function getWorkspacesForGroup(page?: number) {
        if (!userGroupId) {return}
        if (abortController) { abortController.abort(); }
    
        const newAbortController = new AbortController();
        setAbortController(newAbortController);
        setIsWorkspacesLoading(true);

        let skip = 0;
        if (page) { skip = (page - 1) * top; }
    
        let params = {
            searchTerm: "",
            sortBy: workspacesSortToggle[0],
            sort: workspacesSortToggle[1],
            top: top,
            skip: skip,
        };
    
        getWorkspacesForUserGroup(userGroupId, params, { signal: newAbortController.signal })
            .then((data) => {
                setWorkspaceData(data.records);
                setWorkspacePaginationSettings({
                    hasPagination: data.total > data.top,
                    currentPage: Math.ceil(page ?? 1),
                    totalPages: Math.ceil(data.total / data.top),
                    pageSize: data.top,
                    totalItems: data.total,
                });
            })
            .catch((error) => {
                if (error.name !== "AbortError" && error.code !== "ERR_CANCELED") {
                    setNotificationDetails({show: true, status: NotificationStatus.error, message: "Unable to get workspace data for user group, please refresh the page."});
                }
            });
    }

    function getUsers(page?: number){
        if (!userGroupId) { return }
        if (abortController) { abortController.abort(); }
    
        const newAbortController = new AbortController();
        setAbortController(newAbortController);
        setIsLoading(true);

        let skip = 0;
        if (page) { skip = ((page - 1) * top);} 

        let params = {
            searchTerm: searchString,
            sortBy: usersSortToggle[0],
            sort: usersSortToggle[1],
            top: top,
            skip: skip
        };

        getAllUsersForGroup(userGroupId, params, { signal: newAbortController.signal })
        .then((data) =>   {
            setUserData(data.records);
            setPaginationSettings({
                hasPagination: data.total > data.top,
                currentPage:  Math.ceil(page ?? 1),
                totalPages: Math.ceil(data.total / data.top),
                pageSize: data.top,
                totalItems: data.total
            })
        })
        .catch((error) => {
            if (error.name !== "AbortError" && error.code !== "ERR_CANCELED") {
                setNotificationDetails({ show: true, status: NotificationStatus.error, message: "Unable to get user data, please refresh the page."});
            }
        })
    }

    function getModalUsers(paginate: 'new' | 'paginate'){
        if (!userGroupId ) {return}
        setIsModalLoading(true)
  
        const skipValue: number = paginate === 'paginate' ? modalSkip + top : 0;
        const params = {
            searchTerm: searchModalUsersString,
            sortBy: modalSortToggle[0],
            sort: modalSortToggle[1],
            top: top,
            skip: skipValue,
        }
  
        getAllUsersAvailableToAdd(userGroupId, params)
        .then((data) => {
            setModalUserData(paginate === 'paginate' ? [...modalUserData, ...data.records] : data.records);
            setMoreAvailable(data.moreAvailable);
            setModalSkip(data.skip);
            setIsModalLoading(false);
        })
        .catch((error) => {
          if (error.name !== "AbortError" && error.code !== "ERR_CANCELED") {
            setNotificationDetails({ show: true, status: NotificationStatus.error, message: "Unable to get user group data, please refresh the modal."});
          }
        })
    }

    async function addUserToGroup(userId: any, userGroupId: number){
        addUserToOrganisationGroup(userId, userGroupId)
        .then(() => {
          setNotificationDetails({ show: true, status: NotificationStatus.success, message: "Successfully added user to group."});
        })
        .catch(() => {
            setNotificationDetails({ show: true, status: NotificationStatus.error, message: "Unable to add the user to this group, please try again."});
        })
        .finally(() => {
            getUsers();
        })
    }

    async function removeUserFromGroup(userId: any, userGroupId: any){
        removeUserFromOrganisationGroup(userId, userGroupId)
        .then(() => {
          setNotificationDetails({ show: true, status: NotificationStatus.success, message: "Successfully removed user from group."});
        })
        .catch(() => {
            setNotificationDetails({ show: true, status: NotificationStatus.error, message: "Unable to remove user from this group, please try again."});
        })
        .finally(() => {
            getUsers(userData.length > 1 ? paginationSettings.currentPage : paginationSettings.currentPage -1);
        })
    }

    async function removeGroupFromWorkspace(workspaceId: any){
        removeUserGroupFromWorkspaceAccess(workspaceId, userGroupId)
        .then(() => {
          setNotificationDetails({ show: true, status: NotificationStatus.success, message: "Successfully removed usergroup from workspaces."});
        })
        .catch(() => {
            setNotificationDetails({ show: true, status: NotificationStatus.error, message: "Unable to remove usergroup from this workspaces, please try again."});
        })
        .finally(() => {
            getWorkspacesForGroup(workspaceData.length > 1 ? workspacePaginationSettings.currentPage : workspacePaginationSettings.currentPage -1);
        })
    }

    const context = useMemo(() => (
        {userGroupId, setUserGroupId, notificationDetails, setNotificationDetails, getUserGroups, userGroupData, paginationSettings, searchString, setSearchString, sortToggle, setSortToggle, setUserGroupPermission, includeArchivedUserGroups, setIncludeArchivedUserGroups, isLoading, setIsLoading, userData, getUsers, userGroupName, getModalUsers, modalUserData, moreAvailable, searchModalUsersString, setSearchModalUsersString, setIsModalLoading, isModalLoading, modalSortToggle, setModalSortToggle, setUserGroupName, removeUserFromGroup, setGroupCanBeModified, groupCanBeModified, addUserToGroup, usersSortToggle, setUsersSortToggle, getWorkspacesForGroup, workspaceData, workspacesSortToggle, setWorkspacesSortToggle, isWorkspacesLoading, setIsWorkspacesLoading, workspacePaginationSettings, removeGroupFromWorkspace}
    ), [userGroupId, notificationDetails, userGroupData, searchString, paginationSettings, includeArchivedUserGroups, sortToggle, isLoading, userData, userGroupName, modalSortToggle, isModalLoading, searchModalUsersString, modalUserData, moreAvailable, searchModalUsersString, userGroupName, groupCanBeModified, usersSortToggle, workspaceData, workspacesSortToggle, isWorkspacesLoading, workspacePaginationSettings]);
      
    return (
      <OrgSettingsUserGroupsContext.Provider value={context}>
        {children}
      </OrgSettingsUserGroupsContext.Provider>
    );
};

export default OrgSettingsUserGroupsProvider;